"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "./button.tsx";

/**
 * A dialog built on `<dialog>` rather than a portal and a focus trap of our
 * own: the platform already does modality, focus containment and Escape, and
 * a hand-rolled version of those is where accessibility bugs live.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      // `m-auto` is load-bearing: the UA stylesheet centres an open `<dialog>`
      // with `margin: auto`, and Tailwind's preflight resets that to 0, which
      // pins the dialog to the top-left corner.
      className="m-auto w-full max-w-lg rounded-card border border-line bg-surface p-0 text-ink-body backdrop:bg-chrome/40"
    >
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <div className="max-h-[60vh] overflow-y-auto px-5 py-4">{children}</div>
      {footer ? (
        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}

/**
 * The confirmation a destructive action needs. `confirmLabel` is required and
 * unlabelled by default on purpose — "Are you sure?" with an "OK" button tells
 * a user nothing about what is about to happen.
 *
 * `busy` holds the whole dialog while the action runs; `confirmDisabled` holds
 * only the confirm, for a body that asks something before it can proceed. They
 * are separate because the way out of a dialog you cannot yet confirm is
 * Cancel, and `busy` takes that away too.
 */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = true,
  busy = false,
  confirmDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  busy?: boolean;
  confirmDisabled?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            size="sm"
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-ink-body">{body}</div>
    </Modal>
  );
}
